import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { Button, cn } from '../../components/ui/Button';
import supabase from '../../supabase/supabase';
import { emailService } from '../../services/emailService';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import Toast from '../../components/ui/Toast';
import { useToast } from '../../hooks/useToast';
import { 
  format, addMonths, subMonths, startOfMonth, endOfMonth, 
  startOfWeek, endOfWeek, isSameMonth, isBefore, startOfDay, addDays 
} from 'date-fns';

const STEPS = ['Select Barber', 'Choose Service', 'Date', 'Time', 'Location', 'Confirm'];
const libraries = ['places'];

export default function BookingFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  
  const [barbers, setBarbers] = useState([]);
  const [selectedBarberId, setSelectedBarberId] = useState(location.state?.barberId || null);
  
  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [selectedTime, setSelectedTime] = useState(null);
  
  const [address, setAddress] = useState('');
  const [addressNotes, setAddressNotes] = useState('');
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [useNewLocation, setUseNewLocation] = useState(false);
  const [saveLocation, setSaveLocation] = useState(false);
  const [addressTitle, setAddressTitle] = useState('Home');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fetchingBarbers, setFetchingBarbers] = useState(true);
  const [customerCity, setCustomerCity] = useState(null);

  const [dayBookings, setDayBookings] = useState([]);
  const [activeBookings, setActiveBookings] = useState([]);
  
  const [confirmSameDayModal, setConfirmSameDayModal] = useState(null);
  const { toast, showToast, hideToast } = useToast();

  const filterServiceName = location.state?.serviceName;

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries
  });
  const [autocomplete, setAutocomplete] = useState(null);

  useEffect(() => {
    const fetchBarbersAndProfile = async () => {
      setFetchingBarbers(true);
      try {
        const { data: authData } = await supabase.auth.getUser();
        const user = authData?.user;
        
        let cCity = null;

        if (user) {
          const { data: activeBks } = await supabase
            .from('bookings_1740423033529')
            .select('id, booking_date, status')
            .eq('customer_id', user.id)
            .in('status', ['pending', 'confirmed']);
          setActiveBookings(activeBks || []);

          const { data: profile } = await supabase
            .from('profiles_1740423033529')
            .select('address, city, district, saved_addresses')
            .eq('id', user.id)
            .maybeSingle();
            
          let addresses = profile?.saved_addresses || [];
          if (addresses.length === 0 && profile?.address) {
            addresses = [{ title: 'Main', address: profile.address }];
          }
          setSavedAddresses(addresses);
          
          if (addresses.length > 0) {
            setAddress(addresses[0].address);
            setUseNewLocation(false);
          } else {
            setUseNewLocation(true);
          }

          if (profile?.city) {
            setCustomerCity(profile.city);
            cCity = profile.city;
          }
        }

        let query = supabase
          .from('profiles_1740423033529')
          .select('id, full_name, email, rating, avatar_url, address, district, service_cities')
          .eq('role', 'barber')
          .eq('is_available', true);

        if (cCity) {
          query = query.contains('service_cities', JSON.stringify([cCity]));
        }

        if (filterServiceName) {
          const { data: serviceLinks } = await supabase
            .from('services_1740423033529')
            .select('barber_id')
            .eq('name', filterServiceName);
          
          const providerIds = serviceLinks?.map(s => s.barber_id) || [];
          if (providerIds.length > 0) query = query.in('id', providerIds);
          else query = query.eq('id', '00000000-0000-0000-0000-000000000000'); 
        }

        const { data, error } = await query;
        if (error) {
          console.error('Barber fetch error:', error);
          setBarbers([]);
        } else {
          setBarbers(data || []);
        }

        if (selectedBarberId) setCurrentStep(1);
      } finally {
        setFetchingBarbers(false);
      }
    };

    fetchBarbersAndProfile();
  }, [filterServiceName]);

  useEffect(() => {
    if (selectedBarberId) {
      const fetchBarberServices = async () => {
        const { data } = await supabase
          .from('services_1740423033529')
          .select('*')
          .eq('barber_id', selectedBarberId);
        setServices(data || []);

        if (filterServiceName) {
          const match = data?.find(s => s.name === filterServiceName);
          if (match) setSelectedServiceId(match.id);
        }
      };
      fetchBarberServices();
    }
  }, [selectedBarberId, filterServiceName]);

  useEffect(() => {
    if (selectedDate && selectedBarberId) {
      const fetchDayBookings = async () => {
        const { data } = await supabase
          .from('bookings_1740423033529')
          .select('booking_time, status, service:service_id(duration)')
          .eq('barber_id', selectedBarberId)
          .eq('booking_date', selectedDate)
          .neq('status', 'cancelled');
        setDayBookings(data || []);
      };
      fetchDayBookings();
    }
  }, [selectedDate, selectedBarberId]);

  const handleBack = () => {
    if (currentStep === 0) navigate('/app');
    else setCurrentStep(prev => prev - 1);
  };

  const format12h = (time24) => {
    const [h, m] = time24.split(':');
    const hours = parseInt(h, 10);
    return `${hours % 12 || 12}:${m} ${hours >= 12 ? 'PM' : 'AM'}`;
  };

  const parseMins = (str) => {
    if (!str) return 45;
    const match = str.match(/(\d+)/);
    let val = match ? parseInt(match[0], 10) : 45;
    if (str.toLowerCase().includes('hr') || str.toLowerCase().includes('hour')) val = val * 60;
    return val;
  };

  const parseTimeToMins = (t) => {
    const [h, m] = t.split(':');
    return parseInt(h, 10) * 60 + parseInt(m, 10);
  };

  const formatMinsTo24h = (mins) => 
    `${Math.floor(mins / 60).toString().padStart(2, '0')}:${(mins % 60).toString().padStart(2, '0')}`;

  const handleNext = async () => {
    if (currentStep === 0 && !selectedBarberId) return showToast('Please select a barber to continue.', 'error');
    if (currentStep === 1 && !selectedServiceId) return showToast('Please select a service.', 'error');
    if (currentStep === 2) {
      if (!selectedDate) return showToast('Please select a date.', 'error');
      
      const sameDayBooking = activeBookings.find(b => b.booking_date === selectedDate);
      if (sameDayBooking) {
        setConfirmSameDayModal(sameDayBooking);
        return; 
      }
    }
    if (currentStep === 3 && !selectedTime) return showToast('Please select a time slot.', 'error');
    if (currentStep === 4 && !address.trim()) return showToast('Please enter a service address.', 'error');

    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1);
      return;
    }

    setLoading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) throw new Error("Your session has expired. Please log in again.");

      let service = services.find(s => s.id === selectedServiceId);
      let barber = barbers.find(b => b.id === selectedBarberId);

      if (!service && selectedServiceId) {
        const { data } = await supabase.from('services_1740423033529').select('*').eq('id', selectedServiceId).maybeSingle();
        if (data) service = data;
      }
      if (!barber && selectedBarberId) {
        const { data } = await supabase.from('profiles_1740423033529').select('*').eq('id', selectedBarberId).maybeSingle();
        if (data) barber = data;
      }

      if (!service || !barber) throw new Error("Invalid booking data. Please restart.");

      const finalAddress = addressNotes.trim() ? `${address.trim()} (Notes: ${addressNotes.trim()})` : address.trim();

      if (useNewLocation && saveLocation && finalAddress) {
        const newSaved = [...savedAddresses, { title: addressTitle, address: finalAddress }];
        await supabase.from('profiles_1740423033529').update({ saved_addresses: newSaved }).eq('id', user.id);
      }

      const { data: customerProfile } = await supabase
        .from('profiles_1740423033529')
        .select('full_name, email, phone')
        .eq('id', user.id)
        .maybeSingle();

      const { data: bookingData, error } = await supabase
        .from('bookings_1740423033529')
        .insert([{
          customer_id: user.id,
          barber_id: selectedBarberId,
          service_id: selectedServiceId,
          booking_date: selectedDate,
          booking_time: selectedTime,
          total_price: service.price,
          status: 'pending',
          address: finalAddress
        }])
        .select().single();

      if (error) throw error;

      await supabase.from('notifications_1740423033529').insert([{
        user_id: selectedBarberId,
        title: 'New Booking Request',
        message: `${customerProfile?.full_name || 'A customer'} requested a ${service.name} at ${format12h(selectedTime)}.`,
        action_link: '/barber/bookings'
      }]);

      if (barber?.email) {
        await emailService.sendBookingReceivedToBarber({
          barberEmail: barber.email,
          barberName: barber.full_name,
          customerEmail: customerProfile?.email || user.email,
          customerName: customerProfile?.full_name || 'Customer',
          customerPhone: customerProfile?.phone || 'Not provided',
          serviceName: service.name,
          date: selectedDate,
          time: format12h(selectedTime),
          address: finalAddress,
          manageLink: `${window.location.origin}/booking/b/${bookingData.id}`
        });
      }

      setSuccess(true);
      setTimeout(() => navigate('/app/bookings'), 2000);
    } catch (err) {
      showToast(err.message || "An error occurred. Please try again.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const onLoadGoogleMaps = (autoC) => setAutocomplete(autoC);
  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.formatted_address) setAddress(place.formatted_address);
    }
  };

  const getFilteredTimes = () => {
    if (!selectedDate) return [];
    
    const service = services.find(s => s.id === selectedServiceId);
    const myServiceMins = parseMins(service?.duration);

    const slots = [];
    for (let m = 9 * 60; m <= 18 * 60; m += 15) slots.push(formatMinsTo24h(m));

    const now = new Date();
    const isToday = new Date(selectedDate).toDateString() === now.toDateString();
    const currentMinsWithBuffer = (now.getHours() * 60) + now.getMinutes() + 60;

    return slots.filter(t => {
      const slotStart = parseTimeToMins(t);
      const slotEnd = slotStart + myServiceMins + 15;

      if (isToday && slotStart <= currentMinsWithBuffer) return false;

      for (let b of dayBookings) {
        const bStart = parseTimeToMins(b.booking_time);
        const bEnd = bStart + parseMins(b.service?.duration) + 15;
        if (Math.max(slotStart, bStart) < Math.min(slotEnd, bEnd)) return false;
      }
      return true;
    });
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(endOfMonth(monthStart));
    
    const rows = [];
    let days = [];
    let day = startDate;
    const today = startOfDay(new Date());

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const isPast = isBefore(day, today);
        const isSelected = selectedDate === format(day, 'yyyy-MM-dd');
        
        days.push(
          <button 
            key={day.toString()} 
            disabled={isPast}
            onClick={() => {
              if (!isPast) {
                setSelectedDate(format(cloneDay, 'yyyy-MM-dd'));
                setSelectedTime(null);
              }
            }}
            className={cn(
              "p-2 w-10 h-10 flex items-center justify-center rounded-full text-sm font-bold transition-all",
              !isSameMonth(day, monthStart) ? "text-gray-200" : isPast ? "text-gray-300 cursor-not-allowed opacity-50" : "text-brand-dark hover:bg-gray-100",
              isSelected ? "bg-brand-primary text-white hover:bg-brand-primary shadow-md" : ""
            )}
          >
            {format(day, 'd')}
          </button>
        );
        day = addDays(day, 1);
      }
      rows.push(<div className="flex justify-between w-full mb-2" key={day.toString()}>{days}</div>);
      days = [];
    }
    return rows;
  };

  const filteredTimes = getFilteredTimes();

  if (success) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center animate-in fade-in">
      <div className="w-20 h-20 bg-green-50 text-green-600 rounded-full flex items-center justify-center mb-6 border-4 border-green-100">
        <SafeIcon name="Check" className="text-4xl" />
      </div>
      <h2 className="text-3xl font-black mb-2">Request Sent!</h2>
      <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">Barber has been notified</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center items-center relative">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      
      <div className="w-full max-w-md bg-white h-[100dvh] sm:h-[800px] relative shadow-2xl sm:rounded-[2.5rem] overflow-hidden flex flex-col">
        
        <header className="px-6 py-4 flex items-center gap-4 border-b border-gray-100 bg-white">
          <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-full">
            <SafeIcon name="ArrowLeft" />
          </button>
          <div className="flex-1 text-center pr-10">
            <h1 className="text-xl font-black">{STEPS[currentStep]}</h1>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-28">
          
          {/* STEP 0: Select Barber */}
          {currentStep === 0 && (
            <div className="space-y-3">
              {fetchingBarbers ? (
                <div className="text-center py-10 opacity-50 font-bold uppercase text-[10px] tracking-widest">
                  Syncing Pro Network...
                </div>
              ) : barbers.length > 0 ? (
                <>
                  {customerCity && (
                    <div className="mb-4 bg-brand-primary/5 p-3 rounded-xl border border-brand-primary/10 flex items-center gap-2 text-brand-primary text-xs font-bold">
                      <SafeIcon name="MapPin" className="text-sm" /> Barbers servicing <strong className="ml-1">{customerCity}</strong>
                    </div>
                  )}
                  {barbers.map(b => (
                    <div 
                      key={b.id} 
                      onClick={() => setSelectedBarberId(b.id)}
                      className={cn(
                        "p-4 rounded-2xl border-2 cursor-pointer flex items-center gap-4 transition-all",
                        selectedBarberId === b.id ? "border-brand-primary bg-brand-primary/5" : "border-gray-50 hover:border-gray-100"
                      )}
                    >
                      <img src={b.avatar_url || `https://ui-avatars.com/api/?name=${b.full_name}&background=0047FF&color=fff`} className="w-12 h-12 rounded-xl object-cover" alt="" />
                      <div className="flex-1">
                        <p className="font-bold text-brand-dark">{b.full_name}</p>
                        <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">
                          {b.district}
                          {b.service_cities?.length > 0 && (
                            <span className="ml-1 text-brand-primary">
                              • {b.service_cities.slice(0, 2).join(',')} {b.service_cities.length > 2 ? ` +${b.service_cities.length - 2}` : ''}
                            </span>
                          )}
                        </p>
                      </div>
                      {selectedBarberId === b.id && (
                        <div className="w-6 h-6 rounded-full bg-brand-primary flex items-center justify-center flex-shrink-0">
                          <SafeIcon name="Check" className="text-white text-xs" />
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center py-20 px-6">
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-gray-100">
                    <SafeIcon name="Clock" className="text-3xl text-gray-400" />
                  </div>
                  <p className="text-xl text-brand-dark font-black mb-3">
                    No Barbers Available
                  </p>
                  <p className="text-sm text-gray-500 font-medium max-w-sm mx-auto leading-relaxed">
                    We apologize, but there are no barbers available in your selected area at this moment. Please try again later or choose another date.
                  </p>
                  <Button variant="outline" className="mt-8" onClick={() => navigate('/app/profile')}>
                    Update My City
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* STEP 1: Choose Service */}
          {currentStep === 1 && (
            <div className="space-y-3">
              {services.map(s => (
                <div 
                  key={s.id} 
                  onClick={() => setSelectedServiceId(s.id)}
                  className={cn(
                    "p-5 rounded-2xl border-2 cursor-pointer flex justify-between items-center transition-all",
                    selectedServiceId === s.id ? "border-brand-primary bg-brand-primary/5" : "border-gray-50 hover:border-gray-100"
                  )}
                >
                  <div>
                    <p className="font-bold">{s.name}</p>
                    <p className="text-xs text-gray-400 font-bold uppercase">{s.duration}</p>
                  </div>
                  <p className="font-black text-brand-dark">Rs. {Number(s.price).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}

          {/* STEP 2: Date */}
          {currentStep === 2 && (
            <div className="animate-in slide-in-from-right-4">
              <div className="bg-white rounded-[2rem] border border-gray-100 p-6 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 hover:bg-gray-100 rounded-full">
                    <SafeIcon name="ChevronLeft" className="text-gray-500" />
                  </button>
                  <h2 className="text-lg font-black text-brand-dark">{format(currentMonth, "MMMM yyyy")}</h2>
                  <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 hover:bg-gray-100 rounded-full">
                    <SafeIcon name="ChevronRight" className="text-gray-500" />
                  </button>
                </div>
                <div className="flex justify-between w-full mb-4">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                    <div key={d} className="w-10 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">{d}</div>
                  ))}
                </div>
                <div className="space-y-2">{renderCalendar()}</div>
              </div>
            </div>
          )}

          {/* STEP 3: Time */}
          {currentStep === 3 && (
            <div className="animate-in slide-in-from-right-4">
              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700">
                  Select Time for {selectedDate ? format(new Date(selectedDate), "MMM do, yyyy") : ''}
                </label>
                <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">15-minute intervals</p>
              </div>
              
              {filteredTimes.length === 0 ? (
                <div className="bg-orange-50 border border-orange-100 p-6 rounded-2xl text-center">
                  <SafeIcon name="Clock" className="text-3xl text-orange-400 mx-auto mb-2" />
                  <p className="font-bold text-orange-800">No time slots available.</p>
                  <p className="text-xs text-orange-600 mt-1">This barber is fully booked or time has passed.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {filteredTimes.map(t => (
                    <button 
                      key={t} onClick={() => setSelectedTime(t)}
                      className={cn(
                        "py-3 rounded-xl font-bold border-2 transition-all active:scale-95 text-sm",
                        selectedTime === t ? "bg-brand-primary text-white border-brand-primary shadow-lg shadow-brand-primary/20" : "bg-white border-gray-100 text-gray-600 hover:border-brand-primary/30"
                      )}
                    >
                      {format12h(t)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 4: Location */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-in slide-in-from-right-4">
              <label className="block text-sm font-bold text-gray-700">Where should the barber arrive?</label>

              {savedAddresses.length > 0 && (
                <div className="space-y-3 mb-6">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Saved Locations</p>
                  <div className="flex flex-col gap-3">
                    {savedAddresses.map((addr, idx) => (
                      <div 
                        key={idx} onClick={() => { setUseNewLocation(false); setAddress(addr.address); }}
                        className={cn(
                          "p-4 rounded-xl border-2 cursor-pointer transition-all flex items-start gap-3",
                          !useNewLocation && address === addr.address ? "border-brand-primary bg-brand-primary/5" : "border-gray-100 bg-white hover:border-gray-200"
                        )}
                      >
                        <SafeIcon name="MapPin" className={!useNewLocation && address === addr.address ? "text-brand-primary" : "text-gray-400"} />
                        <div>
                          <p className="font-bold text-sm text-brand-dark">{addr.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{addr.address}</p>
                        </div>
                      </div>
                    ))}
                    
                    <div 
                      onClick={() => { setUseNewLocation(true); setAddress(''); setAddressNotes(''); }}
                      className={cn(
                        "p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-3",
                        useNewLocation ? "border-brand-primary bg-brand-primary/5" : "border-gray-100 bg-white hover:border-gray-200"
                      )}
                    >
                      <SafeIcon name="Search" className={useNewLocation ? "text-brand-primary" : "text-gray-400"} />
                      <p className="font-bold text-sm text-brand-dark">Search for a new location</p>
                    </div>
                  </div>
                </div>
              )}

              {(useNewLocation || savedAddresses.length === 0) && (
                <div className="space-y-4 bg-gray-50 p-5 rounded-2xl border border-gray-100">
                  {isLoaded && (
                    <div className="relative">
                      <SafeIcon name="Search" className="absolute left-4 top-4 text-gray-400 z-10" />
                      <Autocomplete onLoad={onLoadGoogleMaps} onPlaceChanged={onPlaceChanged}>
                        <input 
                          type="text" 
                          placeholder="Search for an address on Google Maps..."
                          className="w-full pl-12 pr-4 py-4 border border-brand-primary/20 rounded-2xl outline-none focus:border-brand-primary bg-white font-medium shadow-sm text-sm"
                        />
                      </Autocomplete>
                    </div>
                  )}
                  
                  <textarea 
                    value={address} 
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Enter full service address (door number, street, city)..."
                    className="w-full p-4 border border-gray-200 rounded-2xl outline-none focus:border-brand-primary min-h-[80px] font-medium text-sm bg-white"
                  />
                  
                  <input 
                    type="text" 
                    placeholder="Unit number, floor, or extra directions (optional)"
                    value={addressNotes}
                    onChange={e => setAddressNotes(e.target.value)}
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl bg-white outline-none focus:border-brand-primary font-medium text-sm"
                  />

                  <div className="pt-2 border-t border-gray-200">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={saveLocation}
                        onChange={(e) => setSaveLocation(e.target.checked)}
                        className="w-5 h-5 rounded text-brand-primary outline-none focus:ring-0 cursor-pointer"
                      />
                      <span className="font-bold text-sm text-brand-dark">Save this location for future</span>
                    </label>
                    
                    {saveLocation && (
                      <input 
                        type="text" 
                        placeholder="Title (e.g. Home, Work)"
                        value={addressTitle}
                        onChange={(e) => setAddressTitle(e.target.value)}
                        className="w-full mt-3 p-3 border border-gray-200 rounded-xl outline-none focus:border-brand-primary bg-white text-sm font-bold"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 5: Confirm */}
          {currentStep === 5 && (
            <div className="bg-brand-dark p-8 rounded-[2rem] text-white shadow-2xl relative overflow-hidden animate-in zoom-in-95">
              <h3 className="text-brand-accent font-black uppercase text-[10px] mb-2 tracking-widest relative z-10">Checkout</h3>
              <p className="text-2xl font-black mb-1 relative z-10">
                {services.find(s => s.id === selectedServiceId)?.name || 'Selected Service'}
              </p>
              <p className="text-white/60 text-sm mb-6 relative z-10">
                With {barbers.find(b => b.id === selectedBarberId)?.full_name || 'Selected Barber'}
              </p>

              <div className="bg-white/10 p-4 rounded-xl mb-4 relative z-10">
                <p className="text-[10px] uppercase tracking-widest text-brand-accent font-black mb-1">Schedule</p>
                <p className="text-sm font-medium">
                  {selectedDate ? format(new Date(selectedDate), "MMM do, yyyy") : ''} at {selectedTime ? format12h(selectedTime) : ''}
                </p>
              </div>

              <div className="bg-white/10 p-4 rounded-xl mb-6 relative z-10">
                <p className="text-[10px] uppercase tracking-widest text-brand-accent font-black mb-1">Service Location</p>
                <p className="text-sm font-medium">
                  {addressNotes.trim() ? `${address.trim()} (Notes: ${addressNotes.trim()})` : address.trim()}
                </p>
              </div>

              <div className="mb-6 relative z-10">
                <p className="text-[10px] uppercase tracking-widest text-brand-accent font-black mb-3">Payment Method</p>
                <div className="bg-brand-primary/20 border border-brand-primary p-4 rounded-xl flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-primary/20 flex items-center justify-center text-brand-primary">
                      <SafeIcon name="DollarSign" className="text-lg" />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-white">Cash After Service</p>
                      <p className="text-[10px] text-white/60">Pay directly to barber onsite</p>
                    </div>
                  </div>
                  <div className="w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between font-black text-2xl pt-6 border-t border-white/10 relative z-10">
                <span>Total Due</span>
                <span className="text-brand-accent">Rs. {Number(services.find(s => s.id === selectedServiceId)?.price || 0).toLocaleString()}</span>
              </div>
            </div>
          )}
        </main>

        <div className="absolute bottom-0 left-0 w-full p-6 bg-white border-t border-gray-100">
          <Button 
            className="w-full py-4 font-black text-lg shadow-xl" 
            onClick={handleNext}
            disabled={loading || (currentStep === 0 && barbers.length === 0 && !fetchingBarbers)}
          >
            {loading ? 'Processing...' : currentStep === 5 ? 'Confirm Booking' : 'Continue'}
          </Button>
        </div>
      </div>
      
      {/* Custom Same-Day Booking Modal */}
      {confirmSameDayModal && (
        <div className="fixed inset-0 bg-brand-dark/60 z-50 flex items-center justify-center p-6 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full shadow-2xl animate-in zoom-in-95">
            <div className="w-16 h-16 bg-orange-50 text-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="AlertTriangle" className="text-3xl" />
            </div>
            <h2 className="text-xl font-black text-brand-dark mb-2 text-center">Active Booking Exists</h2>
            <p className="text-gray-500 font-medium text-sm mb-6 text-center leading-relaxed">
              You already have an active booking on this day. Would you like to manage or cancel it first?
            </p>
            <div className="flex flex-col gap-3">
              <Button 
                className="w-full py-4 font-black" 
                onClick={() => navigate(`/booking/c/${confirmSameDayModal.id}`)}
              >
                Manage Existing Booking
              </Button>
              <Button 
                variant="ghost" 
                className="w-full py-3 text-gray-500 font-bold" 
                onClick={() => setConfirmSameDayModal(null)}
              >
                Go Back
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}